// api.js - FIXED VERSION
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiBase';
import i18n from '../i18n';

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Dedupe concurrent identical GETs. Several components independently fetch
// the same collections on mount (e.g. Header's notification-badge polling
// fetches /cars, /clients, /reservations, and so does whichever page is
// open) — this collapses near-simultaneous requests to the same URL into
// one network call instead of firing duplicates, without touching every
// page's own fetch logic. Only plain GETs with no signal/cancelToken are
// deduped, so per-caller request cancellation still works untouched.
const inflightGets = new Map();
const GET_DEDUPE_WINDOW_MS = 2000;

const dedupeGet = (originalGet) => (url, config) => {
  if (config?.signal || config?.cancelToken) {
    return originalGet(url, config);
  }
  const key = `${url}?${JSON.stringify(config?.params || {})}`;
  const now = Date.now();
  const cached = inflightGets.get(key);
  if (cached && now - cached.timestamp < GET_DEDUPE_WINDOW_MS) {
    return cached.promise;
  }
  const promise = originalGet(url, config);
  inflightGets.set(key, { promise, timestamp: now });
  promise.finally(() => {
    setTimeout(() => {
      if (inflightGets.get(key)?.promise === promise) inflightGets.delete(key);
    }, GET_DEDUPE_WINDOW_MS);
  });
  return promise;
};

let isRefreshing = false;
let pendingRefreshPromises = [];

const processPendingRefreshPromises = (error, token = null) => {
  pendingRefreshPromises.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
      return;
    }
    resolve(token);
  });
  pendingRefreshPromises = [];
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Lets the backend translate error messages (duplicate entry, missing
    // field, etc.) into the same language the UI is currently showing.
    config.headers['X-App-Language'] = i18n.language || 'fr';

    // If data is FormData, let the browser set the Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ✅ FIXED: Only ONE response interceptor
api.interceptors.response.use(
  (response) => {
    const method = response?.config?.method?.toLowerCase() || '';
    const url = response?.config?.url || '';

    if ((method === 'put' || method === 'patch') && url.includes('/cars/')) {
      let payload = response?.config?.data;
      let mileageValue = null;

      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (e) {
          payload = null;
        }
      }

      if (payload instanceof FormData) {
        mileageValue = payload.get('mileage');
      } else if (payload && payload.mileage !== undefined && payload.mileage !== null) {
        mileageValue = payload.mileage;
      }

      if (mileageValue !== null && mileageValue !== undefined && mileageValue !== '') {
        const parts = url.split('/').filter(Boolean);
        const carId = parts[parts.length - 1];
        window.dispatchEvent(new CustomEvent('carMileageUpdated', {
          detail: { carId, mileage: mileageValue }
        }));
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    const requestUrl = originalRequest?.url || '';
    const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh');

    // If 401 and not already retrying, try to refresh token (skip for auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRefreshPromises.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((refreshError) => Promise.reject(refreshError));
      }

      isRefreshing = true;

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

        processPendingRefreshPromises(null, accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processPendingRefreshPromises(refreshError, null);

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 403 means "authenticated but not authorized for this one resource" —
    // it must NOT force a logout. A permission-gated endpoint (settings,
    // expenses, a specific report, ...) correctly returning 403 for a user
    // who lacks that one permission used to wipe the whole session and
    // redirect to /login, which turned "you can't see this widget" into
    // "you're logged out everywhere" the moment any permission gate was
    // added to a previously-open endpoint. Let the calling code handle the
    // rejected promise (e.g. show that one section as empty/errored)
    // instead of tearing down the session.

    return Promise.reject(error);
  }
);

api.get = dedupeGet(api.get.bind(api));

export default api;
